<!--
  -  Copyright (c) Qwoach LLC 2021. All rights reserved.
  -  Unauthorized copying of this file, via any medium is strictly prohibited
  -  Proprietary and confidential
  -  Created by Artem Bogush <artem@qwoach.com> on 20/12/2021
  -->

<!--
IMPORTANT!!! SEO-CRITICAL PATH.
IN CASE OF ANY CHANGES, MAKE SURE
TO CHECK PAGE SCORE ON https://pagespeed.web.dev/
-->
<template>
	<div class="h-100 relative" :class="rootClassComputed">
<!--		<MobileStopperScreen v-if="isMobileScreen"/>-->
		<div v-if="hasTeacher" class="mb-3 h3 text-center mt-2">
			<div>Student access by {{teacherName}}</div>
		</div>

		<div class="h-100 relative">
			<GenogramMain
				v-if="shouldRenderGenogramMain"
				ref="genogramMain"
				class="h-100"
				:genogram-item-prop="embeddedGenogramItem"
				:config="genogramMainConfigComputed"
				:embedded="isEmbeddedMode"
				:hide-save-button="isReadonlyMode"
				:readonly="isReadonlyMode"
				:show-watermark="showWatermarkComputed"
				:client-profile="clientProfile"
				:dataManagerProp="localDataManager"
				:analytics-context="genogramAnalyticsContextComputed"
				:analytics-signal-manager="genogramAnalyticsSignalManager"
				:actionListener="actionListener"
				@meaningfulUsageDetected="onMeaningfulUsageDetected"
				@templateAdded="onTemplateAdded"
				@detailsVisibilityChanged="onDetailsVisibilityChanged"
			>
				<template #genogram-overlay>
					<div
						v-if="isTiledExportMaskVisible"
						class="genogram-export-mask">
						<div class="genogram-export-mask__label">Preparing export...</div>
					</div>
				</template>
			</GenogramMain>

			<div v-if="showTopActionPanel"
				 class="absolute genogram-top-action-panel"
				 :style="{'right': '10px', 'top': savePanelTopComputed}">
				<qw-button
					v-if="showDevBlogExportButton"
					class="px-5 mr-2"
					@click="onExportForBlogClicked">
					Export for Blog
				</qw-button>

				<qw-link-button
					v-if="shouldShowStudentAccessLink"
					class="mr-3 genogram-student-access-link"
					@click="onStudentAccessClicked">
					Student? Get Free Access
				</qw-link-button>

				<qw-button
					v-if="isLoggedIn"
					class="px-5"
					@click="goToDashboard">
					My Genograms
				</qw-button>

<!--				font-size-4 is needed for SEO. Otherwise it complains about accessibility issue becuase of low contrast between words and background-->
				<qw-button
					type="primary"
					class="px-5 font-size-4"
					@click="onSaveClicked">
					{{ saveButtonTextComputed }}
				</qw-button>
			</div>

			<div v-if="showTutorialButton"
				 class="fixed genogram-tutorial-button"
				 style="left: 10px; bottom: 50px; z-index: 9;">

				<qw-button
					type="primary"
					style="width: 185px"
					class="px-5 font-size-4"
					@click="showVideoDialogClicked">
					{{showTutorialTitleComputed}}
				</qw-button>
			</div>
		</div>
		<div v-if="showFeedbackButton" class="genogram-feedback-button">
			<GatherFeedbackView
				style="z-index: 50"
				@feedbackClicked="onFeedbackClicked"
			/>
		</div>

		<div v-if="aiDevTest && isDevEnvFn() && isFullAppLoaded && isJsonCommandPanelShown"
			 class="bottom-left-fixed"
			 style="z-index: 9999999;width: 500px;">
			<div class="card p-3">
				<FormTextRow
					style="max-height: 200px; overflow: auto;"
					:multiline="true"
					placeholder="Type your genogram description in plain language"
					:min-rows="4"
					v-model="aiTestInput"
				/>
				<qw-button
					type="primary"
					@click="onRunCommandClicked">
					Build my Genogram
				</qw-button>
			</div>

		</div>

		<div v-if="aiDevTest && isDevEnvFn() && isFullAppLoaded"
			 class="bottom-left-fixed json-command-panel-wrap">
			<div
				class="card p-2 json-command-panel"
				:class="{'json-command-panel--collapsed': !isJsonCommandPanelShown}">
				<div class="json-command-panel__header" :class="{'json-command-panel__header--collapsed': !isJsonCommandPanelShown}">
					<div class="qw-row json-command-panel__header-top">
						<div class="json-command-panel__copy">
							<div class="json-command-panel__title">JSON commands</div>
						</div>
						<div class="layout-spacer"></div>
						<div class="json-command-panel__toggle-action">
							<qw-link-button @click="changeJsonCommandPanelVisibility">{{ jsonCommandPanelVisibilityButtonTitle }}</qw-link-button>
						</div>
					</div>
				</div>

				<div v-if="isJsonCommandPanelShown">
					<qw-switch
						v-model="showHandwritingSpaceUi"
						class="mb-3"
						active-text="Handwritten notes"/>
					<FormTextRow
						class="json-command-panel__input"
						:multiline="true"
						placeholder="Paste JSON instructions"
						:min-rows="4"
						v-model="jsonCommandsInput"
					/>
					<qw-button
						type="primary"
						@click="onRunJsonCommandClicked">
						Run commands
					</qw-button>
				</div>
			</div>

		</div>

		<LoadingIndicator/>




		<span v-if="isLoadingGlobal">
			<div id="loading" style="background: rgba(255, 255, 255, 0.8)">
				<div class="spinner">
					<div class="bounce1"></div>
					<div class="bounce2"></div>
					<div class="bounce3"></div>
					<span class="text text-center">
						<div style="padding: 5px; background: white">Loading... Just a moment...</div>

					</span>
				</div>

			</div>
		</span>
	</div>

</template>

<script>

import BaseComponent from "@/core/components/BaseComponent.vue";
import GenogramMain from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramMain.vue";
import ClientProfile from "@shared/Profile/ClientProfile";

import LocalDataManager from "@/core/js/backend/LocalDataManager";
import {createDialogLazy} from "@/utils/helpers-external";
import LoadingIndicator from "@/core/components/LoadingIndicator.vue";
let VideoDialog = () => import("@/core/components/Dialogs/QwoachDialog/VideoDialog.vue");
import MobileStopperScreen from "@/tools/MobileStopperScreen.vue";

let videoDialog = createDialogLazy(VideoDialog)
import {GenogramToolId} from "@shared/Profile/CoachingToolsConstants.js";
import {fromBase64, isDevEnv, isObject, jsonParseSecure, logError, timeNow} from "@/utils/helpers";
import {GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} from "@/core/js/model/SharedConstants";
import {SettingNameEnum} from "@shared/enums/SettingNameEnum";
import {isMobileScreen} from "@/core/screenHelper";
import {SettingsCookie} from "@/core/js/Cookie/SettingsCookie";
import GatherFeedbackView from "@/tools/FeedbackGather/GatherFeedbackView.vue";
import {messageDialog} from "@/core/components/Dialogs/dialog";
import teacherMapObj from "@/core/modules/Login/TeacherMap";
import FormTextRow from "@/core/components/Form/FormTextRow.vue";
import {consumeGenogramEmbedPayload, getGenogramEmbedPayload, parseGenogramEmbedBoolean} from "@/tools/GenogramTool/GenogramEmbedStorage";
import {
	buildGenogramJsonData,
	createGenogramItemFromCommands,
	createGenogramItemFromInput,
	createGenogramItemFromJson,
	getDefaultGenogramJsonData
} from "@/tools/GenogramTool/lib/GenogramInputConverter";
import genogramExportCommandBuilder from "@/tools/GenogramTool/lib/GenogramExportCommandBuilder";
import GenogramAnalyticsSignalManager
	from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramAnalyticsSignalManager";
import GenogramReviewFlowManager from "@/tools/GenogramTool/review/GenogramReviewFlowManager";
import {setShowStudentAccess, shouldShowStudentAccess} from "@/tools/GenogramTool/GenogramStudentAccessPromptHelper";
import {hasClientSessionMarker} from "@/core/js/helpers/ClientSessionMarkerHelper";

let CreateCoachAccountDialog = () => import("@/core/modules/Login/CreateCoachAccountDialog.vue");
let CreateAccountGenogramDialog = () => import("@/tools/CreateAccountFreeToolsDialog.vue");
let GenogramSurveyDialog = () => import("@/tools/GenogramTool/GenogramSurveyDialog.vue");
let StudentGenogramEnrolDialogV2 = () => import("@/core/modules/Login/StudentGenogramEnrolDialogV2.vue");

let createAccountGenogramDialog = createDialogLazy(CreateCoachAccountDialog)
let getClientNameDialog = createDialogLazy(CreateAccountGenogramDialog)
let genogramSurveyDialog = createDialogLazy(GenogramSurveyDialog)
let studentGenogramEnrolDialogV2 = createDialogLazy(StudentGenogramEnrolDialogV2)


export default {
	name: "GenogramToolMain",
	components: {FormTextRow, GatherFeedbackView, MobileStopperScreen, LoadingIndicator, GenogramMain},
	extends: BaseComponent,

	props: {
		specialId: String,
		settingsParam: {Object, default: () => ({})},
		utm_params: {Object, default: () => {}},
		teacherId: String,
		aiDevTest: Boolean, //show json command input to test AI integration
		embedded: [Boolean, String],
		readonly: [Boolean, String],
		qwk: String,
		payloadKey: String,
	},

	data() {
		return {
			isLoadingGlobal: false,
			cookieSettings: new SettingsCookie(),
			clientProfile: new ClientProfile(),
			localDataManager: new LocalDataManager(),
			genogramAnalyticsSignalManager: new GenogramAnalyticsSignalManager(this.$analyticManager),
			genogramReviewFlowManager: new GenogramReviewFlowManager(this),
			embeddedGenogramItem: null,
			isEmbedBootstrapReady: false,
			isTransportBootstrapDisabled: false,
			isDirty: true,
			canShowTutorial: true,
			noTrialPlanMonthly: "895944",
			noTrialPlanYearly: "897164",
			settings: {},
			loadFullAppPromise: null,
			isFullAppLoaded: false,
			aiTestInput: "",
			jsonCommandsInput: "",
			isJsonCommandPanelShown: false,
			isTiledExportMaskVisible: false,
			showHandwritingSpaceUi: this.$route?.query?.showHandwritingSpace === "true",
			teacherMapData: {},
			hasCompletedPaymentFlow: false,
			localShowStudentAccess: null,
		}
	},

	async created() {
		this.cleanupTransportQueryParam()

		if(this.teacherId && !this.hasTeacher){
			try{
				let teacherMapData = await this.dataManager.apiGetTeacherInfo({
					teacherId: this.teacherId,
				})
				if(teacherMapData) {
					this.teacherMapData = {
						...this.teacherMapData,
						[this.teacherId]: teacherMapData,
					}
				}
				console.log("GenogramToolMain.created: this.teacherMapData = ", this.teacherMapData, ", teacherMapData = ", teacherMapData)
			} catch (error) {
				let _ = logError(error)
			}
		}

		let options = {first_contact: 'genogram_tools'}
		if(this.hasTeacher){
			options.teacherId = this.teacherId
			options.teacherUid = this.teacherUid
			if(!this.teacherData.isDisabled){
				options.studentAccess = this.teacherData.access || "freeTrial" //we default to free trial if teacher is not disabled.
			}
		}

		this.$analyticManager.setUserPropsOnce(options)

		this.cookie.setOnce('qw_ft', "genogram_tools");
		this.saveSettingsFromUrl()
		this.restoreSettings()
		this.setupUtmParams()
		this.setupCampaignData()
		await this.setupEmbeddedGenogram()
		this.isEmbedBootstrapReady = true
		this.genogramAnalyticsSignalManager.startWorkPeriod(this.genogramAnalyticsContextComputed)

		this.$analyticManager.trackEvent("Genogram tools opened")
		this.genogramAnalyticsSignalManager.trackToolOpened(this.genogramAnalyticsContextComputed)

		if(this.shouldWarnOnUnload){
			window.addEventListener('beforeunload', this.beforeWindowUnload)
			window.addEventListener('onbeforeunload', this.beforeWindowUnload)
		}

		this.cookie.setOnce('qw_ft', "genogram_tools");
		// if(isDevEnv){
		// 	this.dataManager.apiPostTestAmplitudeEventPropsMerge({
		// 		amplitudeDeviceId: this.$analyticManager.getAmplitudeDeviceId()
		// 	})
		// }

		// we don't load it like this because it lags the user interaction which badly impacts SEO (the INP metric)
		// setTimeout(() => {
		// 	this.loadFullApp()
		// }, 8000)

		// if(isMobileScreen()){
		// 	setTimeout(() => {
		// 		let instructions = document.querySelector("#genogramInstructions")
		// 		console.log("GenogramToolMain.created: instructions = ", instructions)
		// 		if(instructions){
		// 			instructions.innerHTML += "<br/><br/>Tip: switch to landscape or open on desktop for better experience."
		// 		}
		// 	}, 8000)
		// }


	},

	mounted() {
		if(!isMobileScreen()){
			// this.showVideoDialog()
		}
		this.restoreReturningUserIfNeeded()
	},

	beforeUnmount () {
		window.removeEventListener('beforeunload', this.beforeWindowUnload)
		window.removeEventListener('onbeforeunload', this.beforeWindowUnload)
	},

	// beforeRouteLeave (to, from, next) {
	// 	if (this.isDirty) {
	// 		next(false)
	// 		window.location = to.path // this is the trick
	// 	} else {
	// 		next()
	// 	}
	// },


	methods: {
		isMobileScreen,
		isDevEnvFn(){
			return isDevEnv
		},

		cleanupTransportQueryParam() {
			if (!this.qwk || typeof window === "undefined") {
				return
			}

			let url = new URL(window.location.href)
			if (!url.searchParams.has("qwk")) {
				return
			}

			url.searchParams.delete("qwk")
			let nextUrl = `${url.pathname}${url.search}${url.hash}`
			window.history.replaceState(window.history.state, "", nextUrl)
		},

		async setupEmbeddedGenogram() {
			let payload = this.shouldKeepTransportPayload
				? getGenogramEmbedPayload(this.transportKey)
				: consumeGenogramEmbedPayload(this.transportKey)
			if (!payload) {
				this.disableTransportBootstrap()
				return
			}

			let genogramItem = null
			try {
				genogramItem = await this.createEmbeddedGenogramItem(payload)
			} catch (error) {
				this.disableTransportBootstrap()
				return
			}

			if (!genogramItem) {
				this.disableTransportBootstrap()
				return
			}

			this.embeddedGenogramItem = genogramItem
			await this.localDataManager.setGenogramItem(genogramItem)
		},

		disableTransportBootstrap() {
			this.isTransportBootstrapDisabled = true
		},

		async createEmbeddedGenogramItem(payload) {
			let parsedInput = await genogramExportCommandBuilder.parseSeoBlogEmbedGenogram(payload)
			return await createGenogramItemFromInput(parsedInput.input, this.clientProfile.id)
		},

		shouldShowHandwritingSpace() {
			return this.showHandwritingSpaceUi === true
		},

		createGenogramItemFromJson(genogramJson) {
			return createGenogramItemFromJson(genogramJson, this.clientProfile.id)
		},

		async createGenogramItemFromCommands(instructions) {
			return createGenogramItemFromCommands(instructions, this.clientProfile.id)
		},

		buildGenogramJsonData(genogramData) {
			return buildGenogramJsonData(genogramData)
		},

		getDefaultGenogramJsonData() {
			return getDefaultGenogramJsonData()
		},

		async onExportForBlogClicked() {
			await this.loadFullApp({progress: true})
			await this.clearGenogramSelection()
			let genogramItem = this.getCurrentGenogramItem()
			let blogBlock = await genogramExportCommandBuilder.buildSeoBlogEmbedGenogram(genogramItem)
			if (!blogBlock) {
				return
			}
			await this.copyToClipboard(blogBlock, "Blog genogram block copied to clipboard.")
		},

		async clearGenogramSelection() {
			let cyEditor = this.$refs.genogramMain?.$refs?.cyEditor?.getCyEditor?.()
			cyEditor?.unselectAll?.()
			// Cytoscape selection state needs a brief moment to settle.
			await this.wait(50)
		},

		wait(ms) {
			return new Promise((resolve) => setTimeout(resolve, ms))
		},

		async onRunCommandClicked(){
			this.showProgress("Loading...")

			try{


				let instructions = await this.dataManager.apiPostAiPromptToGenogramInstructions({prompt: this.aiTestInput})

				const { default: GenogramLayoutManager } = await import("@/tools/GenogramTool/GenogramLayoutManager");


				let aiInput = instructions//jsonParseSecure(this.aiTestInput)
				console.log("GenogramToolMain.onRunCommandClicked: aiInput = ", aiInput)
				let genogramLayoutManager = new GenogramLayoutManager()
				let elementsLayout = genogramLayoutManager.layoutFamilyTree(aiInput)
				let {genogramData, toDelete} = genogramLayoutManager.familyTreeToGenogramData(elementsLayout)

				console.log("GenogramToolMain.onRunCommandClicked: elementsLayout = ", elementsLayout)
				console.log("GenogramToolMain.onRunCommandClicked: genogramData = ", genogramData, ", toDelete = ", toDelete)

				this.$refs.genogramMain.setGenogramData(genogramData, toDelete)
			}finally {
				this.hideProgress()
			}
		},

		async onRunJsonCommandClicked(){
			this.showProgress("Loading...")

			try{
				let parsedInput = await genogramExportCommandBuilder.parseSeoBlogEmbedGenogram(this.jsonCommandsInput)
				let genogramItem = await createGenogramItemFromInput(parsedInput.input, this.clientProfile.id)
				this.$refs.genogramMain?.applyGenogramItem?.(genogramItem)
				await this.localDataManager.setGenogramItem(genogramItem)
			}finally {
				this.hideProgress()
			}
		},

		changeJsonCommandPanelVisibility() {
			this.isJsonCommandPanelShown = !this.isJsonCommandPanelShown
		},

		async actionListener({action}, prams) {
			console.log("GenogramToolMain.actionListener: action = ", action)
			switch (action) {
				case "loadFullApp":
					await this.loadFullApp({progress: true})
					return true
				case "exportGenogram":
					await this.loadFullApp({progress: true})
					if(!this.isLoggedIn && !this.isDevBlogExportMode){
						console.log(`${GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} GenogramToolMain: export blocked because user not logged in`)
						await messageDialog({
							title: "Please first save genogram to export it",
							message: `Click "${this.saveButtonTextComputed}" on the right to save and export it`
						})
						console.log("GenogramToolMain.actionListener: not logged in")
						return false
					}

					this.isLoadingGlobal = true
					console.log(`${GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} GenogramToolMain: export started`, {
						clientId: this.clientProfile?.id,
					})
					return true
				case "exportGenogramCompleted":
					this.isLoadingGlobal = false
					console.log(`${GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} GenogramToolMain: export completed`, {
						clientId: this.clientProfile?.id,
					})
					await this.genogramReviewFlowManager.maybeRunPromptFlow("genogram_exported")
					return true
				case "setTiledExportMaskVisible":
					this.isTiledExportMaskVisible = !!prams?.isVisible
					return true

			}


		},

		async onFeedbackClicked(){
			await this.loadFullApp({progress: true})
		},

		async onDetailsVisibilityChanged(isVisible) {
			await this.loadFullApp({progress: true})
		},

		async onTemplateAdded(isVisible) {
			await this.loadFullApp({progress: true})
		},

		async loadFullApp(options = {}) {
			if(this.isEmbeddedMode){
				return
			}
			if(!this.loadFullAppPromise){
				// eslint-disable-next-line no-async-promise-executor
				this.loadFullAppPromise = new Promise(async (resolve) => {
					let {progress} = options
					if(progress){
						this.isLoadingGlobal = true
					}
					let analyticManager = this.$analyticManager
					await import("@/entry/importFull.js")

					// we need to ensure all the dialogs are loaded at this point
					// to prevent cases when a new version deploys while user works
					// on a genogram which will prevent them from saving the genogram if not preloaded
					// (chunks with the dialogs may be unavailable in this case)
					await Promise.all([
						CreateCoachAccountDialog(),
						CreateAccountGenogramDialog(),
						GenogramSurveyDialog(),
					])


					console.log("GenogramToolMain.loadFullApp: this.$analyticManager new = ", this.$analyticManager, ", analyticManager = ", analyticManager)
					if(typeof analyticManager.flushEventsTo === "function"){
						analyticManager.flushEventsTo(this.$analyticManager)
					}
					this.genogramAnalyticsSignalManager.setAnalyticsManager(this.$analyticManager)
					// await import("@/entry/elementui-imports")

					await this.onFullAppLoaded()
					this.isLoadingGlobal = false
					resolve()
				})
			}

			await this.loadFullAppPromise

		},

		restoreReturningUserIfNeeded() {
			if(hasClientSessionMarker()){
				this.loadFullApp({progress: true})
			}
		},

		async restoreAuthStateAfterFullAppLoad() {
			if(this.isLoggedIn){
				return this.userProfile
			}
			return await new Promise((resolve) => {
				let unsubscribe = null
				let didResolve = false
				let resolveOnce = (userProfile = null) => {
					if(didResolve){
						return
					}
					didResolve = true
					if(unsubscribe){
						unsubscribe()
					}
					resolve(userProfile)
				}
				unsubscribe = this.$authManager.startListenForLoginStateChange((userProfile) => {
					resolveOnce(userProfile)
				})
			})
		},

		async onFullAppLoaded(){
			await this.restoreAuthStateAfterFullAppLoad()
			// clientProfile will not have id since we don't load full DataManager initially for SEO performance.
			// so we set it here if needed
			this.isFullAppLoaded = true
			console.log("GenogramToolMain.onFullAppLoaded before: clientProfile = ", this.clientProfile, ", this.clientProfile.id = ", this.clientProfile.id)
			if(!this.clientProfile.id){
				this.clientProfile.generateId()
				let genogramItem = this.getGenogramItem()
				genogramItem.setClientId(this.clientProfile.id)
			}
			console.log("GenogramToolMain.onFullAppLoaded after: clientProfile = ", this.clientProfile, ", this.clientProfile.id = ", this.clientProfile.id)
			this.$analyticManager.trackEvent("Genogram full app loaded")

		},

		saveSettingsFromUrl(){
			let queryOptions = this.$route.query.opt || ""
			let querySettings = jsonParseSecure(fromBase64(queryOptions)) || {}
			console.log("GenogramToolMain.saveSettingsFromUrl: querySettings = ", querySettings, ", queryOptions = ", queryOptions)

			let settings = this.cookieSettings.get() || {}
			let extraOptions = {}
			if(this.$route.query.utm_ad && !settings.utm_ad){
				extraOptions.utm_ad = this.$route.query.utm_ad
			}

			settings = {
				...settings,
				...this.settingsParam,
				...querySettings,
				...extraOptions}
			this.cookieSettings.set(settings)
		},

		restoreSettings(){
			let savedSettings = this.cookieSettings.get() || {}
			this.settings = {...savedSettings, ...this.settings}

			console.log("GenogramToolMain.restoreSettings: settings = ", this.settings)
		},

		setupUtmParams(){
			if(isObject(this.utm_params)){

				let utmParams = {}
				Object.keys(this.utm_params).forEach(key => {
					utmParams[key] = this.utm_params[key]
					utmParams[`initial_${key}`] = this.utm_params[key]
				})

				this.$analyticManager.setUserPropsOnce(utmParams)
			}
		},

		setupCampaignData(){

			let planMonthly = this.noTrialPlanMonthly
			let planYearly = this.noTrialPlanYearly

			if(this.settings["m"]){
				this.settings[SettingNameEnum.PLAN_ID_YEARLY] = this.settings["y"]
			}
			if(this.settings["y"]){
				this.settings[SettingNameEnum.PLAN_ID_MONTHLY] = this.settings["m"]
			}
			if(this.settings["pm"]){
				this.settings[SettingNameEnum.PRICE_MONTHLY] = this.settings["pm"]
			}
			if(this.settings["py"]){
				this.settings[SettingNameEnum.PRICE_YEARLY] = this.settings["py"]
			}
			if(this.settings["se"]){
				this.settings[SettingNameEnum.SHOW_STUDENT_ENROLLMENT] = this.settings["se"]
			}

			//enabled it for tests on 10 Oct 2024
			//upd 19 Oct - payment performance dropped. reverting to standard settings
			// this.settings[SettingNameEnum.SHOW_STUDENT_ENROLLMENT] = true


			//force no trial plan for tests on 12 Oct 2024
			//testing if this will encourage more students to share enrollment with their teachers

			//19 Oct - performance dropped. Reenable free trial for the ad "genogram_7d_trial"
			if(!this.settings[SettingNameEnum.PLAN_ID_YEARLY]
				&& this.settings["utm_ad"] !== "genogram_7d_trial"){
				this.settings[SettingNameEnum.PLAN_ID_MONTHLY] = planMonthly
			}


			if(this.settings["no-trial"]){
				this.settings[SettingNameEnum.PLAN_ID_MONTHLY] = planMonthly
				this.settings[SettingNameEnum.PLAN_ID_YEARLY] = planYearly
			}
			if(this.settings["no-trial-monthly"]){
				this.settings[SettingNameEnum.PLAN_ID_MONTHLY] = planMonthly
			}
			if(this.settings["no-trial-yearly"]){
				this.settings[SettingNameEnum.PLAN_ID_YEARLY] = planYearly
			}
			if(this.settings["disable-monthly"]) {
				delete this.settings[SettingNameEnum.PLAN_ID_MONTHLY]
			}
			if(this.settings["disable-yearly"]) {
				delete this.settings[SettingNameEnum.PLAN_ID_YEARLY]
			}

			// if(isDevEnv){
			// 	this.settings[SettingNameEnum.PLAN_ID_MONTHLY] = mapPlanDev(this.settings[SettingNameEnum.PLAN_ID_MONTHLY])
			// 	this.settings[SettingNameEnum.PLAN_ID_YEARLY] = mapPlanDev(this.settings[SettingNameEnum.PLAN_ID_YEARLY])
			// }


			if(this.settings["tutorial-off"]){
				this.canShowTutorial = false
			}

			console.log("GenogramToolMain.setupCampaignData: settings = ", this.settings)
		},

		showVideoDialogClicked() {
			let _ = this.$analyticManager.trackEvent("Button clicked: show tutorial video")
			this.showVideoDialog()
		},

		async showVideoDialog() {
			if(!this.canShowTutorial){
				return
			}
			await this.loadFullApp({progress: true})
			let _ = this.$analyticManager.trackEvent("Tutorial video opened")
			await videoDialog({
				width: "560",
				showCancel: false,
				title: "How to create a genogram (in under 4 minutes)",
				subTitle: isMobileScreen() ? "Tip: switch to landscape mode or open on desktop for better experience.<br/>To add a symbol, just tap it, and then tap on the canvas.<br/><br/>" : "",
				// gDriveVideoUrl: "https://drive.google.com/file/d/1MqDD7vA6l--9XPwIY9NQX0177r5wYLUw/preview",
				youtubeUrl: "https://www.youtube.com/embed/jImpP6bY8Eg",
				params: {
					width: "500px",
					height: "315px"
				},
				description: "<h4>The video covers how to</h4>" +
					"00:09 - Add a person to your Genogram<br/>" +
					"00:29 - Add family relationships<br/>" +
					"00:45 - List of available emotional and family relationships<br/>" +
					"01:17 - Add twins/siblings<br/>" +
					"01:39 - Add emotional relationships<br/>" +
					"02:05 - Add multiple emotional relationships for the same people<br/>" +
					"02:21 - Add relationships with arrow (directional relationships)<br/>" +
					"02:45 - Remove a relationship connection<br/>" +
					"02:54 - Labels, age, notes and other properties of a person<br/>" +
					"03:04 - Add custom Health Conditions<br/>" +
					"04:07 - Legend - auto generated<br/>"
			})
		},

		beforeWindowUnload(e) {
			if(!this.shouldWarnOnUnload){
				return
			}
			console.log("beforeWindowUnload: this.isDirty = ", this.isDirty, ", e = ", e)
			if (this.isDirty) {
				e.preventDefault()
				e.returnValue = ''
				this.$analyticManager.trackEvent("Leave without saving warning shown")
			}
		},
		async onSaveClicked() {
			await this.loadFullApp({progress: true})
			if(this.isTeacherEnrollment()){
				return this.goOptions({
					name: "GenogramForClassEnroll",
					query: this.teacherEnrollmentQuery()
				})
			}
			let _ = this.$analyticManager.trackEvent("Genogram tools save clicked")
			// await this.showSurvey()F

			// if(isDevEnv){
			// 	await this.showInstructionsDialog()
			// }
			let genogramItem = this.getGenogramItem()
			try{
				this.$analyticManager.trackEvent("Saving genogram", {genogram: JSON.stringify(genogramItem)})
				let amplitudeUserId = this.$analyticManager.getAmplitudeDeviceId()
				console.log("amplitudeUserId = ", amplitudeUserId)
				this.dataManager.apiPostLogInfo({name: "Saving genogram", genogram: JSON.stringify(genogramItem), amplitudeUserId})
			} catch (error){
				let _ = logError(error)
			}

			let clientName = await getClientNameDialog({
				purposeQuestion: "What's your role? (please be as specific as possible)",
				itemNameProp: "Genogram",
				firstTouch: "genogram_tools",
				// onAccountCreated: () => this.onAccountCreated()
			})

			if (!clientName) {
				return
			}

			this.clientProfile.name = clientName
			this.clientProfile.toolsSettings.enableCoachingTool(GenogramToolId)


			if(this.studentClassPlanId){
				this.settings[SettingNameEnum.PLAN_ID_MONTHLY] = this.studentClassPlanId
			}

			if(this.hasTeacher){
				if(this.isStudentAccessFreeTrial){
					this.settings[SettingNameEnum.EXTRA_MESSAGE] = `Special Student Free Trial by ${this.teacherName}`
				}
				this.$analyticManager.trackEvent("Teacher referral", {teacherId: this.teacherId, teacherName: this.teacherName})
			}

			let planIdMonthly = this.settings[SettingNameEnum.PLAN_ID_MONTHLY]
			let planIdYearly = this.settings[SettingNameEnum.PLAN_ID_YEARLY]


			console.log("planIdMonthly = ", planIdMonthly)

			let userProfile

			let isNewAccount = false

			if(this.currentUser && this.userProfile){
				userProfile = this.userProfile
			} else {

				userProfile = await createAccountGenogramDialog({
					planIdMonthly: planIdMonthly || undefined,
					planIdYearly: planIdYearly || undefined,
					productName: "EasyGenogram by Qwoach",
					skipCreditCard: this.studentSkipCreditCard,
					actionButtonTextProp: this.actionButtonTextProp,
					canShowNoCCMessage: !this.isStudentAccessFull,
					canShowStudentEnrollDialog: !this.hasTeacher,
					settings: this.settings,
					lockedAccountCreated: (userProfile) => this.onLockedAccountCreated(userProfile),
					paymentCompleted: () => this.onPaymentCompleted(),
					studentAccessChanged: (value) => this.setLocalStudentAccessPrompt(value),
					teacherMap: this.teacherMap,
					userExtraProps: {
						firstTouch: "genogram_tools",
						account: "genogram",
						...this.studentExtraProps,
					}
				})
				isNewAccount = true
			}


			if (!userProfile) {
				return
			}

			await this.handleReviewBlockAfterPaymentIfNeeded(isNewAccount)

			// if(clientName){
			if (genogramItem) {
				if(genogramItem.clientId !== this.clientProfile.id){
					genogramItem.setClientId(this.clientProfile.id)
				}
				if(isNewAccount){
					await this.onAccountCreated()
				}
				await this.handleAsync(async () => {

					if(this.hasTeacher){
						this.$analyticManager.trackEvent("Teacher referral - signed up", {teacherId: this.teacherId, teacherName: this.teacherName})
					}


						await this.dataManager.createClientProfile(this.clientProfile)
						await this.dataManager.setGenogramItem(genogramItem)
						let tagsContainer = await this.localDataManager.getTagsContainerItem()
						await this.dataManager.setTagsContainer(tagsContainer)
						await this.saveCustomConnectionsToAccount()
						this.$refs.genogramMain?.trackSaveAnalytics({
							save_source: isNewAccount ? "standalone_tool" : "existing_account",
							has_teacher_referral: this.hasTeacher,
						})
						// if(isNewAccount){
					// 	await this.showSurvey()
					// }
					this.isDirty = false


					// we can't use this.go() because GenogramToolMain.vue is part of the optimized genogram entry point that doesn't include routes related to ClientDetails.
					// await this.go("ClientDetailsMainGenogram", {clientProfileId: this.clientProfile.id})

					let host = window.location.origin
					let clientId = this.clientProfile.id

					let url = `${host}/recovery/clients/${clientId}/details/genogram?genogramInstructions=true`

					window.location = url


					// await this.showInstructionsDialog()

				})
			}
			// }

		},

		async onMeaningfulUsageDetected(params = {}) {
			let genogramId = params?.genogramItem?.id || params?.genogramItem?.clientId || this.clientProfile?.id
			console.log(`${GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} GenogramToolMain: meaningfulUsageDetected received`, {
				genogramId,
				params,
			})
			await this.genogramReviewFlowManager.registerMeaningfulUsage(genogramId, params?.meaningful_editing_detected_at)
		},

		onPaymentCompleted() {
			this.hasCompletedPaymentFlow = true
			console.log(`${GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} GenogramToolMain: payment completed flag set`, {
				hasCompletedPaymentFlow: this.hasCompletedPaymentFlow,
			})
		},

		async handleReviewBlockAfterPaymentIfNeeded(isNewAccount) {
			if(!isNewAccount || !this.hasCompletedPaymentFlow){
				console.log(`${GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} GenogramToolMain: payment block skipped`, {
					isNewAccount,
					hasCompletedPaymentFlow: this.hasCompletedPaymentFlow,
				})
				return
			}
			console.log(`${GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} GenogramToolMain: applying payment block`, {
				isNewAccount,
			})
			await this.genogramReviewFlowManager.markPaymentCompleted()
			this.hasCompletedPaymentFlow = false
			console.log(`${GENOGRAM_REVIEW_DIAGNOSTIC_PREFIX} GenogramToolMain: payment block applied`, {
				hasCompletedPaymentFlow: this.hasCompletedPaymentFlow,
			})
		},

		getPlanId() {
			return this.settings[SettingNameEnum.PLAN_ID_MONTHLY] || undefined
		},

		async showSurvey() {

			// await genogramSurveyDialog()
			// this.toast("Thanks a lot! Your answers will help us improve!")
		},



		async onLockedAccountCreated(userProfile) {
			this.$analyticManager.trackEvent("Genogram locked account created")
			let customConnections = await this.localDataManager.getGenogramCustomConnections()
			await this.dataManager.apiPostCreateGenogramAndClientItem({
				userId: userProfile.id,
				genogramItem: this.getGenogramItem(),
				clientProfile: this.clientProfile,
				customConnections,
			})
			this.isDirty = false
			// this.$analyticManager.setUserPropsOnce({account: "genogram"})
		},

		getGenogramItem(){
			return this.$refs.genogramMain.genogramItem
		},

		getCurrentGenogramItem() {
			return this.$refs.genogramMain?.getCurrentGenogramItem?.() || this.getGenogramItem()
		},

		async saveCustomConnectionsToAccount() {
			let customConnections = await this.localDataManager.getGenogramCustomConnections()
			for(let customConnection of customConnections || []){
				await this.dataManager.setGenogramCustomConnectionItem(customConnection)
			}
		},

		onAccountCreated(userProfile) {
			this.$analyticManager.trackEvent("Genogram account created")
			// this.$analyticManager.setUserPropsOnce({account: "genogram"})
		},



		async goToDashboard() {
			let _ = this.$analyticManager.trackEvent("Genogram tools go to dashboard clicked")
			await this.go("CoachMain")
		},

		async onStudentAccessClicked() {
			this.$analyticManager.trackEvent("Student access clicked", {context: "genogram_tool"})
			await studentGenogramEnrolDialogV2({
				studentName: this.userProfile?.name,
				studentEmail: this.userProfile?.email,
				onStudentAccessAccepted: () => this.setStudentAccessPrompt(true),
				onStudentAccessHidden: () => this.setStudentAccessPrompt(false),
			})
		},

		setLocalStudentAccessPrompt(value) {
			this.localShowStudentAccess = value === true
		},

		shouldShowStudentAccessFromProfile() {
			return shouldShowStudentAccess(this.userProfile)
		},

		async setStudentAccessPrompt(value) {
			this.setLocalStudentAccessPrompt(value)
			if(this.isLoggedIn && this.userProfile?.id){
				setShowStudentAccess(this.userProfile, value)
				await this.$authManager.updateUserProfile(this.userProfile)
			}
		},

		isTeacherEnrollment(){
			return !!this.$route.query.enrollmentId || this.$route.query.enrollment === "t"
		},

		teacherEnrollmentQuery() {
			let query = {}
			if(this.$route.query.enrollmentId){
				query.enrollmentId = this.$route.query.enrollmentId
			}
			return query
		},

		shouldPreserveViewport() {
			return this.$route?.query?.preserveViewport === "true"
		}
	},

	computed: {
		genogramAnalyticsContextComputed() {
			let entryPoint = "standalone_tool"
			if(this.isTeacherEnrollment()){
				entryPoint = "teacher_enrollment"
			} else if(this.isEmbeddedMode){
				entryPoint = "embedded"
			}

			return {
				entry_point: entryPoint,
				is_embedded_mode: this.isEmbeddedMode,
				is_readonly_mode: this.isReadonlyMode,
				is_logged_in: this.isLoggedIn,
				is_dev_blog_export_mode: this.isDevBlogExportMode,
				has_teacher_referral: this.hasTeacher,
				teacher_access_type: this.teacherData?.access || null,
				has_transport_bootstrap: !!this.embeddedGenogramItem,
				open_source: this.embeddedGenogramItem ? "embedded_bootstrap" : "new",
				is_existing_genogram: false,
			}
		},

		isEmbeddedMode() {
			return parseGenogramEmbedBoolean(this.embedded)
		},

		isReadonlyMode() {
			return parseGenogramEmbedBoolean(this.readonly)
		},

		showTopActionPanel() {
			return !this.isEmbeddedMode && !this.isReadonlyMode
		},

		isDevBlogExportMode() {
			return isDevEnv && (this.getQueryParam("devBlogExport") === "true" || this.aiDevTest)
		},

		showDevBlogExportButton() {
			return this.isDevBlogExportMode
		},

		showWatermarkComputed() {
			return !this.isDevBlogExportMode
		},

		showTutorialButton() {
			return this.canShowTutorial && !this.isEmbeddedMode && !this.isReadonlyMode
		},

		showFeedbackButton() {
			return !this.isEmbeddedMode
		},


		shouldShowStudentAccessLink() {
			return !this.hasTeacher && (this.localShowStudentAccess === true || this.shouldShowStudentAccessFromProfile())
		},

		shouldWarnOnUnload() {
			return !this.isEmbeddedMode && !this.isReadonlyMode
		},

		shouldRenderGenogramMain() {
			if (!this.needsPayloadBootstrap) {
				return true
			}
			return this.isEmbedBootstrapReady && !!this.embeddedGenogramItem
		},

		needsPayloadBootstrap() {
			return !!this.transportKey
		},

		shouldKeepTransportPayload() {
			return isDevEnv && this.getQueryParam("keepPayload") === "true"
		},

		transportKey() {
			if (this.isTransportBootstrapDisabled) {
				return null
			}
			return this.qwk || this.payloadKey || null
		},

		rootClassComputed() {
			return {
				"genogram-tool-main-root": true,
				"genogram-tool-main-root-embedded": this.isEmbeddedMode,
			}
		},

		genogramMainConfigComputed() {
			return {
				fitOnOpen: !!this.transportKey && !this.shouldPreserveViewport(),
				snapGrid: !this.isEmbeddedMode,
				showHandwritingSpace: this.shouldShowHandwritingSpace(),
			}
		},

		jsonCommandPanelVisibilityButtonTitle() {
			return this.isJsonCommandPanelShown ? "Hide" : "Show"
		},

		saveButtonTextComputed() {
			if(this.isTeacherEnrollment()){
				return isMobileScreen() ? "Enroll" : "Enroll My Class"
			}

			return  isMobileScreen() ? "Save" : "Save Genogram"
		},

		savePanelTopComputed() {
			return this.isMobileScreen() ? "50px" : "5px"
		},

		savePanelBottomComputed() {
			return this.isMobileScreen() ? "5px" : null
		},

		showTutorialTitleComputed() {
			return this.isMobileScreen() ? "Tutorial" : "Show Tutorial"
		},


		teacherMap(){
			let teacherMapData = this.teacherMapData || {}

			let map = {
				...teacherMapObj,
				...teacherMapData
			}
			Object.keys(map).forEach((key) => {
				let teacher = map[key]
				console.log("GenogramToolMain.teacherMap: teacher = ", teacher, ", key = ", key)
				if(teacher.isDisabled){
					delete map[key]
				}
			})
			console.log("GenogramToolMain.teacherMap: map = ", map)
			return map
		},


		teacherData() {
			return this.teacherMap[this.teacherId] || {}
		},
		teacherName() {
			return this.teacherData.name || ""
		},
		className() {
			return this.teacherData.class || "class"
		},
		programName() {
			return this.teacherData.programName || "genogram"
		},
		teacherUid() {
			return this.teacherData.userId
		},
		hasTeacher() {
			return this.teacherMap[this.teacherId]
		},

		studentSkipCreditCard(){
			return this.isStudentAccessFull
		},
		actionButtonTextProp(){
			return this.isStudentAccessFull ? "Get Free Student Access" : null
		},

		studentClassPlanId() {
			if(this.isStudentAccessFreeTrial){
				return "793798"
			}
			return null
		},

		isStudentAccessFreeTrial(){
			return this.hasTeacher && (this.isStudentFullAccessExpired || this.teacherData.access === "freeTrial" || this.teacherData.access == null)
		},

		isStudentAccessFull(){
			return this.hasTeacher && (!this.isStudentFullAccessExpired && this.teacherData.access === "classPaid")
		},
		isStudentFullAccessExpired(){
			return this.teacherData.fullAccessValidTillMs && this.teacherData.fullAccessValidTillMs < timeNow()
		},

		studentExtraProps() {
			let props = {}
			if(this.hasTeacher){
				props = {
					...props,
					isStudent: true,
					teacherUid: this.teacherUid,
					teacherIdPublic: this.teacherId,
					genogramAccess: this.teacherData.access,
				}
			}
			return props
		},

	}
}
</script>

<style lang="scss">
.json-command-panel-wrap{
	z-index: 9999999;
	width: 500px;
}

.json-command-panel{
	max-height: calc(80vh - 200px);
	min-height: 14rem;
}

.json-command-panel--collapsed{
	max-height: initial;
	min-height: 2.8rem;
}

.json-command-panel__header{
	margin-bottom: 0;
	border-bottom: 1px solid #e9eef5;
}

.json-command-panel__header--collapsed{
	border-bottom-color: transparent;
}

.json-command-panel__header-top{
	align-items: flex-start;
}

.json-command-panel__copy{
	min-width: 0;
}

.json-command-panel__title{
	font-size: 14px;
	font-weight: 700;
	line-height: 1.2;
	color: #243b53;
}

.json-command-panel__toggle-action{
	padding-top: 2px;
}

.json-command-panel__toggle-action .link-button{
	font-size: 12px;
	line-height: 1.2;
	color: #7b8794;
}

.json-command-panel__input{
	max-height: 200px;
	overflow: auto;
}

.genogram-tool-main-root-embedded {
	height: 100vh;
	overflow: hidden;
}

.genogram-export-mask{
	position: absolute;
	inset: 0;
	z-index: 1002;
	background: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	pointer-events: all;
}

.genogram-export-mask__label{
	padding: 10px 14px;
	border: 1px solid #dfe3e8;
	background: #fff;
}
</style>
